import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';

interface DraftExportOptions {
  title: string;
  content: string;
  draftType?: string;
  metadata?: {
    createdAt?: string;
    updatedAt?: string;
    author?: string;
  };
}

export const exportDraftToDocx = async (options: DraftExportOptions) => {
  const { title, content, draftType, metadata } = options;

  // Split content into paragraphs
  const contentLines = content.split('\n');

  // Create document sections
  const docSections: Paragraph[] = [];

  // Add title
  docSections.push(
    new Paragraph({
      text: title,
      heading: HeadingLevel.TITLE,
      spacing: {
        after: 200,
      },
    })
  );

  // Add metadata if provided
  if (draftType) {
    docSections.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Type: ${draftType.replace('_', ' ')}`,
            italics: true,
            color: '666666',
          }),
        ],
        spacing: {
          after: 100,
        },
      })
    );
  }

  if (metadata?.updatedAt) {
    docSections.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Last Updated: ${new Date(metadata.updatedAt).toLocaleString()}`,
            italics: true,
            color: '666666',
          }),
        ],
        spacing: {
          after: 200,
        },
      })
    );
  }

  // Add a separator line
  docSections.push(
    new Paragraph({
      text: '',
      border: {
        bottom: {
          color: 'CCCCCC',
          space: 1,
          style: 'single',
          size: 6,
        },
      },
      spacing: {
        after: 200,
      },
    })
  );

  // Add content paragraphs
  contentLines.forEach((line) => {
    docSections.push(
      new Paragraph({
        text: line || ' ', // Use space for empty lines to maintain spacing
        spacing: {
          after: 120,
        },
      })
    );
  });

  // Create the document
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: docSections,
      },
    ],
  });

  // Generate and save the document
  try {
    const blob = await Packer.toBlob(doc);
    const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error exporting to DOCX:', error);
    throw error;
  }
};
